import React from 'react';
import {
  Box, Typography, Paper, Button, Alert, CircularProgress, TextField,
  Accordion, AccordionSummary, AccordionDetails, Divider, Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import VideoPlayer from '../common/VideoPlayer';
import { recordingAPI } from '../../utils/api';
import { formatToBogotaTime } from '../../utils/dateUtils';

export const calculateAssessmentAverage = (questions) => {
  const gradedQuestions = questions.filter((q) => q.grade);
  if (gradedQuestions.length === 0) return null;

  const total = gradedQuestions.reduce(
    (sum, q) => sum + q.grade.score_percentage,
    0
  );
  return (total / gradedQuestions.length).toFixed(2);
};

const AssessmentReview = ({
  assignment, readOnly = false, limitedAccess = false, exportMode = false,
  snapshots = {}, grading = {}, onExportGrades, onExportPDF, isExportingPDF = false,
}) => {
  const { t } = useTranslation();
  const {
    gradeInputs = {}, editingFeedback = {}, tempFeedback = {}, setTempFeedback,
    submitting, autoGrading = {}, resettingRecordingId, handleGradeChange,
    handleOpenResetDialog, handleStartEdit, handleSaveEdit, handleCancelEdit,
    handleSubmitGrade, handleAutoGrade,
  } = grading;
  const Container = exportMode ? Box : Accordion;
  const Header = exportMode ? Box : AccordionSummary;
  const Content = exportMode ? Box : AccordionDetails;

  return (
    <Container {...(!exportMode && { defaultExpanded: true })}>
      <Header {...(!exportMode && { expandIcon: <ExpandMoreIcon /> })} sx={exportMode ? { p: 2 } : undefined}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', ...(exportMode && { flexWrap: 'wrap' }) }}>
          <Typography variant="h6">{assignment.assessment_name}</Typography>
          <Chip
            label={assignment.status}
            color={assignment.status === 'completed' ? 'success' : 'warning'}
            size="small"
          />
          {(() => {
            // Only show average if viewer has full access or not in read-only mode
            if (limitedAccess) {
              return null;
            }
            const avg = calculateAssessmentAverage(assignment.questions);
            return avg !== null && (
              <Chip
                label={`Average: ${avg}%`}
                color="primary"
                size="small"
              />
            );
          })()}
          {limitedAccess && assignment.total_questions && (
            <Chip
              label={`Viewing 1 of ${assignment.total_questions} questions`}
              color="warning"
              size="small"
            />
          )}

          {!exportMode && (
            <>
              {/* Spacer to push export buttons to right */}
              <Box sx={{ flexGrow: 1 }} />

              {/* Export Excel button */}
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  onExportGrades(assignment);
                }}
                sx={{ mr: 1 }}
              >
                {t('admin.grading.exportExcel', 'Export Excel')}
              </Button>

              {/* Export PDF button */}
              <Button
                size="small"
                variant="outlined"
                startIcon={isExportingPDF ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  onExportPDF(assignment);
                }}
                disabled={isExportingPDF}
              >
                {t('admin.grading.exportPDF', 'Export PDF')}
              </Button>
            </>
          )}
        </Box>
      </Header>
      <Content sx={exportMode ? { p: 2 } : undefined}>
        {assignment.questions.map((question, index) => (
          <Box key={question.question_id} sx={{ mb: 4 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle1" gutterBottom>
              Question {index + 1}: {question.question_text}
            </Typography>

            <Paper sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Recording Timeline:
              </Typography>
              {question.recording.started_at && (
                <Typography variant="body2">
                  <strong>Started:</strong> {formatToBogotaTime(question.recording.started_at)}
                </Typography>
              )}
              {question.recording.recorded_at && (
                <Typography variant="body2">
                  <strong>Completed:</strong> {formatToBogotaTime(question.recording.recorded_at)}
                </Typography>
              )}
              {question.recording.started_at && question.recording.recorded_at && (
                <Typography variant="body2" color="primary">
                  <strong>Duration:</strong> {Math.round((new Date(question.recording.recorded_at) - new Date(question.recording.started_at)) / 1000 / 60)} minutes
                </Typography>
              )}
              {question.recording.system_audio_captured === false && (
                <Chip
                  label="System audio not captured"
                  color="warning"
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
            </Paper>

            {/* Reset Question Button - Admin Only */}
            {!readOnly && !exportMode && question.recording.recorded_at && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<RestartAltIcon />}
                  onClick={() => handleOpenResetDialog(
                    question.recording.id,
                    question.question_text
                  )}
                  disabled={resettingRecordingId === question.recording.id}
                >
                  {resettingRecordingId === question.recording.id ? 'Resetting...' : 'Reset Question'}
                </Button>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  This will delete the recording and grade, allowing the candidate to reanswer.
                </Typography>
              </Box>
            )}

            {question.recording.comments && (
              <Paper sx={{ p: 2, mb: 2, bgcolor: '#f5f9ff' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Candidate Comments:
                </Typography>
                <Typography variant="body2" sx={exportMode ? { whiteSpace: 'pre-wrap' } : undefined}>{question.recording.comments}</Typography>
              </Paper>
            )}

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Merged Recording:
              </Typography>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                This video includes the screen recording with webcam overlay (if provided) and audio mix (if provided).
              </Typography>
              {exportMode ? (
                snapshots[question.recording.id] ? (
                  <Box component="img"
                    src={snapshots[question.recording.id]}
                    alt={t('admin.grading.exportVideoFrame', 'First frame of candidate recording')}
                    sx={{ display: 'block', width: '100%', maxHeight: 400, objectFit: 'contain', bgcolor: '#000', borderRadius: 1 }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t('admin.grading.exportVideoUnavailable', 'Video preview unavailable')}
                  </Typography>
                )
              ) : (
                <VideoPlayer
                  url={recordingAPI.getScreenRecording(question.recording.id)}
                  title="Candidate Recording"
                  type="video"
                />
              )}
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Grade:
              </Typography>

              {/* Score input row */}
              <Box sx={{ mb: 2 }}>
                {exportMode ? (
                  <Typography variant="body1">
                    <strong>Score (%): </strong>
                    {question.grade?.score_percentage ?? t('admin.grading.notGraded', 'Not Graded')}
                  </Typography>
                ) : (
                  <TextField
                    label="Score (%)"
                    type="number"
                    value={gradeInputs[question.recording.id]?.score ?? ''}
                    onChange={(e) =>
                      handleGradeChange(question.recording.id, 'score', e.target.value)
                    }
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                    sx={{
                      width: 150,
                      ...(readOnly && {
                        '& .MuiInputBase-input.Mui-disabled': {
                          WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                        },
                      }),
                    }}
                    disabled={readOnly}
                    InputProps={{
                      readOnly: readOnly,
                    }}
                  />
                )}
              </Box>

              {/* Feedback textarea - click-to-edit markdown rendering */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                  Feedback
                </Typography>

                {editingFeedback[question.recording.id] && !readOnly && !exportMode ? (
                  /* Edit mode: TextField with Save/Cancel */
                  <Box>
                    <TextField
                      multiline
                      rows={10}
                      value={tempFeedback[question.recording.id] || ''}
                      onChange={(e) =>
                        setTempFeedback((prev) => ({
                          ...prev,
                          [question.recording.id]: e.target.value
                        }))
                      }
                      sx={{ width: '100%', mb: 1 }}
                      placeholder="Enter feedback in markdown format..."
                      autoFocus
                    />
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={() => handleCancelEdit(question.recording.id)}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={() => handleSaveEdit(question.recording.id)}
                      >
                        Save
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  /* View mode: Rendered markdown with Edit button (if not readOnly) */
                  <Paper
                    sx={{
                      p: 2,
                      width: '100%',
                      minHeight: exportMode ? 0 : 240,
                      maxHeight: exportMode ? 'none' : 600,
                      overflow: exportMode ? 'visible' : 'auto',
                      bgcolor: '#f5f5f5',
                      border: '1px solid rgba(0, 0, 0, 0.23)',
                      position: 'relative',
                      '&:hover .edit-button': {
                        opacity: !readOnly && !exportMode ? 1 : 0
                      },
                      '& h1': { fontSize: '1.5rem', fontWeight: 600, mt: 2, mb: 1 },
                      '& h2': { fontSize: '1.25rem', fontWeight: 600, mt: 2, mb: 1 },
                      '& h3': { fontSize: '1.1rem', fontWeight: 600, mt: 1.5, mb: 0.5 },
                      '& p': { mb: 1 },
                      '& ul, & ol': { pl: 3, mb: 1 },
                      '& li': { mb: 0.5 },
                      '& code': {
                        bgcolor: '#e0e0e0',
                        p: 0.5,
                        borderRadius: 1,
                        fontSize: '0.875rem'
                      },
                      '& pre': {
                        bgcolor: '#e0e0e0',
                        p: 1.5,
                        borderRadius: 1,
                        overflow: 'auto'
                      },
                      '& strong': { fontWeight: 600 },
                      '& em': { fontStyle: 'italic' },
                      '& table': {
                        borderCollapse: 'collapse',
                        width: '100%',
                        mb: 2,
                        mt: 1
                      },
                      '& th': {
                        bgcolor: '#d0d0d0',
                        fontWeight: 600,
                        p: 1,
                        border: '1px solid #999',
                        textAlign: 'left'
                      },
                      '& td': {
                        p: 1,
                        border: '1px solid #ccc',
                        textAlign: 'left'
                      },
                      '& tr:nth-of-type(even)': {
                        bgcolor: '#f9f9f9'
                      }
                    }}
                  >
                    {!readOnly && !exportMode && (
                      <Button
                        className="edit-button"
                        size="small"
                        variant="contained"
                        startIcon={<EditIcon />}
                        onClick={() => handleStartEdit(question.recording.id)}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          opacity: 0,
                          transition: 'opacity 0.2s',
                          zIndex: 1
                        }}
                      >
                        Edit
                      </Button>
                    )}
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {(exportMode ? question.grade?.feedback : gradeInputs[question.recording.id]?.feedback) || 'No feedback provided'}
                    </ReactMarkdown>
                  </Paper>
                )}
              </Box>

              {/* Action buttons - aligned right */}
              {!readOnly && !exportMode && (
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    onClick={() => handleSubmitGrade(question.recording.id)}
                    disabled={submitting || autoGrading[question.recording.id]}
                  >
                    {question.grade ? t('admin.grading.updateGrade', 'Update Grade') : t('admin.grading.submitGrade', 'Submit Grade')}
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => handleAutoGrade(question.recording.id)}
                    disabled={submitting || autoGrading[question.recording.id]}
                    startIcon={autoGrading[question.recording.id] ? <CircularProgress size={16} /> : <SmartToyIcon />}
                  >
                    {autoGrading[question.recording.id]
                      ? t('admin.grading.autoGrading', 'Auto-Grading...')
                      : t('admin.grading.autoGradeButton', 'Auto-Grade with LLM')}
                  </Button>
                </Box>
              )}
            </Box>

            {question.grade && (
              <Alert
                severity={question.grade.auto_graded ? 'info' : 'success'}
                sx={{ mt: 2 }}
                icon={question.grade.auto_graded ? <SmartToyIcon /> : undefined}
              >
                {question.grade.auto_graded && (
                  <Chip
                    label={t('admin.grading.autoGraded', 'Auto-Graded')}
                    size="small"
                    color="secondary"
                    sx={{ mr: 1 }}
                  />
                )}
                {t('admin.grading.previouslyGraded', 'Previously graded')}: {question.grade.score_percentage}% {t('admin.grading.on', 'on')}{' '}
                {formatToBogotaTime(question.grade.graded_at)}
              </Alert>
            )}
          </Box>
        ))}
      </Content>
    </Container>
  );
};

export default AssessmentReview;
